import React, { useState, useEffect } from 'react';
import { 
  Box, 
  MenuItem, 
  Checkbox, 
  FormControlLabel, 
  InputAdornment, 
  Typography,
  Tooltip
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch } from '../../../../store';
import { useSidebar } from '../../../../context/SidebarContext';
import { useBreadcrumb } from '../../../../context/BreadcrumbContext';
import { fetchSystemModules, selectSystemModules } from '../../../../modules/superadmin';
import { createSubscriptionPlan } from '../../../../modules/subscription';
import Loader from '../../../../components/common/Loader';

import {
  PageWrapper,
  HeaderSection,
  PageTitle,
  FormContainer,
  SectionHeader,
  SectionContent,
  FormGroup,
  Label,
  StyledTextField,
  StyledSelect,
  FeaturesHeader,
  FeatureSearch,
  FeatureList,
  FeatureItem,
  TypeBadge,
  ActionButtons,
  CancelButton,
  CreateButton,
  EnabledCountBadge
} from './styles';

const CreateSubscriptionPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch: AppDispatch = useDispatch();
  const { setActivePage } = useSidebar();
  const { setPath, setIsShow } = useBreadcrumb();

  const systemModules = useSelector(selectSystemModules);
  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState({
    plan_name: '',
    plan_description: '',
    plan_price_month: '',
    plan_expiry_in_days: 30,
    plan_status: 'Active',
    users_allowed: 0,
    workers_allowed: 'Unlimited',
    facilities_count: 'Unlimited',
  });

  const [selectedModules, setSelectedModules] = useState<Record<number, boolean>>({});
  const [searchTerm, setSearchTerm] = useState('');
  const [showEnabledOnly, setShowEnabledOnly] = useState(false);
  const [selectedType, setSelectedType] = useState('All');

  useEffect(() => {
    setActivePage('subscriptions');
    setPath([
      { title: 'Super Admin', path: '/super-admin' },
      { title: 'Subscriptions', path: '/super-admin/subscriptions' },
      { title: 'Create Plan', path: '/super-admin/subscriptions/create' },
    ]);
    setIsShow(true);
    dispatch(fetchSystemModules());
  }, [dispatch, setActivePage, setPath, setIsShow]);

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const [lastSelectedIndex, setLastSelectedIndex] = useState<number | null>(null);

  const handleModuleToggle = (moduleId: number, index: number, event: React.MouseEvent | React.ChangeEvent) => {
    // Check if it's a mouse event with shift key
    const isShiftPressed = (event as any).nativeEvent instanceof MouseEvent && (event as any).nativeEvent.shiftKey;

    setSelectedModules(prev => {
      const newState = { ...prev };
      const currentStatus = !prev[moduleId];

      if (isShiftPressed && lastSelectedIndex !== null) {
        const start = Math.min(lastSelectedIndex, index);
        const end = Math.max(lastSelectedIndex, index);
        
        for (let i = start; i <= end; i++) {
          const modId = filteredModules[i].module_id;
          newState[modId] = currentStatus;
        }
      } else {
        newState[moduleId] = currentStatus;
      }
      
      return newState;
    });

    setLastSelectedIndex(index);
  };

  const handleSubmit = async () => {
    setLoading(true);
    const payload = {
      plan_name: formData.plan_name,
      plan_slug: formData.plan_name.toLowerCase().replace(/\s+/g, '-'),
      plan_description: formData.plan_description,
      plan_price_month: parseFloat(formData.plan_price_month) || 0,
      plan_expiry_in_days: formData.plan_expiry_in_days,
      number_of_staff_users: formData.users_allowed,
      number_of_workers: formData.workers_allowed === 'Unlimited' ? -1 : parseInt(formData.workers_allowed as string),
      number_of_facilities: formData.facilities_count === 'Unlimited' ? -1 : parseInt(formData.facilities_count as string),
      modules_list: Object.entries(selectedModules)
        .filter(([_, enabled]) => enabled)
        .map(([id]) => ({
          system_module_id: parseInt(id),
          have_read: true,
          have_write: true,
          have_edit: true,
          have_delete: true,
          have_admin: true
        }))
    };

    try {
      const resultAction = await dispatch(createSubscriptionPlan(payload));
      if (createSubscriptionPlan.fulfilled.match(resultAction)) {
        navigate('/super-admin/subscriptions');
      }
    } finally {
      setLoading(false);
    }
  };

  const filteredModules = systemModules.filter(mod => {
    const matchesSearch = mod.module_name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesEnabled = showEnabledOnly ? selectedModules[mod.module_id] : true;
    
    const modType = (mod.module_type || '').toLowerCase().replace(/[_\s]/g, '');
    const filterType = selectedType.toLowerCase().replace(/[_\s]/g, '');
    const matchesType = selectedType === 'All' || modType === filterType;
    
    return matchesSearch && matchesEnabled && matchesType;
  });

  const enabledCount = Object.values(selectedModules).filter(Boolean).length;

  return (
    <PageWrapper>
      {loading && <Loader open={loading} />}
      <HeaderSection>
        <PageTitle>Create Subscription Plan</PageTitle>
      </HeaderSection>

      <FormContainer>
        {/* Section 1: Plan Details */}
        <SectionHeader>Plan Details</SectionHeader>
        <SectionContent>
          <FormGroup gridColumn="span 6">
            <Label>Plan Name</Label>
            <StyledTextField 
              placeholder="Enter Plan Name" 
              fullWidth 
              size="small"
              value={formData.plan_name}
              onChange={(e) => handleInputChange('plan_name', e.target.value)}
            />
          </FormGroup>
          <FormGroup gridColumn="span 6">
            <Label>Description</Label>
            <StyledTextField 
              placeholder="Describe the plan" 
              fullWidth 
              size="small"
              value={formData.plan_description}
              onChange={(e) => handleInputChange('plan_description', e.target.value)}
            />
          </FormGroup>
          <FormGroup gridColumn="span 4">
            <Label>Price</Label>
            <StyledTextField 
              placeholder="$ 0.00" 
              fullWidth 
              size="small"
              value={formData.plan_price_month}
              onChange={(e) => handleInputChange('plan_price_month', e.target.value)}
              InputProps={{
                startAdornment: <InputAdornment position="start">$</InputAdornment>,
              }}
            />
          </FormGroup>
          <FormGroup gridColumn="span 4">
            <Label>Validity</Label>
            <StyledSelect 
              value={formData.plan_expiry_in_days}
              size="small"
              onChange={(e) => handleInputChange('plan_expiry_in_days', e.target.value)}
              displayEmpty
            >
              <MenuItem value={30}>30 Days</MenuItem>
              <MenuItem value={90}>90 Days</MenuItem>
              <MenuItem value={180}>180 Days</MenuItem>
              <MenuItem value={365}>365 Days</MenuItem>
            </StyledSelect>
          </FormGroup>
          <FormGroup gridColumn="span 4">
            <Label>Plan Status</Label>
            <StyledSelect 
              value={formData.plan_status}
              size="small"
              onChange={(e) => handleInputChange('plan_status', e.target.value)}
            >
              <MenuItem value="Active">Active</MenuItem>
              <MenuItem value="Inactive">Inactive</MenuItem>
            </StyledSelect>
          </FormGroup>
        </SectionContent>

        {/* Section 2: Usage Limits */}
        <SectionHeader>Usage Limits</SectionHeader>
        <SectionContent>
          <FormGroup gridColumn="span 4">
            <Label sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              Users Allowed 
              <Tooltip title="Maximum staff users allowed">
                <InfoOutlinedIcon sx={{ fontSize: '14px', color: '#999' }} />
              </Tooltip>
            </Label>
            <StyledTextField 
              type="number" 
              fullWidth 
              size="small"
              value={formData.users_allowed}
              onChange={(e) => handleInputChange('users_allowed', parseInt(e.target.value) || 0)}
            />
          </FormGroup>
          <FormGroup gridColumn="span 4">
            <Label sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              Workers Allowed
              <Tooltip title="Maximum workers allowed">
                <InfoOutlinedIcon sx={{ fontSize: '14px', color: '#999' }} />
              </Tooltip>
            </Label>
            <StyledSelect 
              value={formData.workers_allowed}
              size="small"
              onChange={(e) => handleInputChange('workers_allowed', e.target.value)}
            >
              <MenuItem value="Unlimited">Unlimited</MenuItem>
              <MenuItem value="100">Up to 100</MenuItem>
              <MenuItem value="500">Up to 500</MenuItem>
              <MenuItem value="1000">Up to 1000</MenuItem>
            </StyledSelect>
          </FormGroup>
          <FormGroup gridColumn="span 4">
            <Label sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              Facilities Count
              <Tooltip title="Maximum facilities allowed">
                <InfoOutlinedIcon sx={{ fontSize: '14px', color: '#999' }} />
              </Tooltip>
            </Label>
            <StyledSelect 
              value={formData.facilities_count}
              size="small"
              onChange={(e) => handleInputChange('facilities_count', e.target.value)}
            >
              <MenuItem value="Unlimited">Unlimited</MenuItem>
              <MenuItem value="5">Up to 5</MenuItem>
              <MenuItem value="10">Up to 10</MenuItem>
              <MenuItem value="25">Up to 25</MenuItem>
            </StyledSelect>
          </FormGroup>
        </SectionContent>

        {/* Section 3: Plan Features */}
        <SectionHeader>Plan Features</SectionHeader>
        <FeaturesHeader sx={{ justifyContent: 'flex-start', gap: 2 }}>
          <EnabledCountBadge>{enabledCount} feature enabled</EnabledCountBadge>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <FeatureSearch 
              placeholder="Search features"
              value={searchTerm}
              size="small"
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <SearchIcon sx={{ color: '#999', fontSize: '20px' }} />
                  </InputAdornment>
                ),
              }}
            />
            <StyledSelect
              value={selectedType}
              size="small"
              onChange={(e) => setSelectedType(e.target.value as string)}
              sx={{ width: '180px', height: '32px' }}
            >
              <MenuItem value="All">All Types</MenuItem>
              <MenuItem value="super_admin">Superadmin</MenuItem>
              <MenuItem value="organization">Organization</MenuItem>
              <MenuItem value="facility">Facility</MenuItem>
            </StyledSelect>
            <FormControlLabel
              control={
                <Checkbox 
                  size="small" 
                  checked={showEnabledOnly}
                  onChange={(e) => setShowEnabledOnly(e.target.checked)}
                />
              }
              label={<Typography sx={{ fontSize: '0.875rem', whiteSpace: 'nowrap' }}>Show enabled only</Typography>}
              sx={{ ml: 0 }}
            />
          </Box>
        </FeaturesHeader>

        <FeatureList>
          {filteredModules.map((mod, index) => (
            <FeatureItem 
              key={mod.module_id}
              onClick={(e) => handleModuleToggle(mod.module_id, index, e)}
              sx={{ cursor: 'pointer', '&:hover': { backgroundColor: '#F5F7F6' } }}
            >
              <Typography sx={{ fontWeight: 500, fontSize: '0.9rem' }}>{mod.module_name}</Typography>
              <Typography sx={{ color: '#666', fontSize: '0.85rem' }}>{mod.module_description || 'No description'}</Typography>
              <TypeBadge type={mod.module_type || 'Organization'}>
                {mod.module_type || 'Organization'}
              </TypeBadge>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Checkbox 
                  checked={!!selectedModules[mod.module_id]}
                  onChange={(e) => handleModuleToggle(mod.module_id, index, e)}
                  onClick={(e) => e.stopPropagation()} // Prevent double trigger when clicking checkbox directly
                  sx={{ color: '#DDD', '&.Mui-checked': { color: '#5E8E7E' } }}
                />
              </Box>
            </FeatureItem>
          ))}
          {filteredModules.length === 0 && (
            <Box sx={{ p: 4, textAlign: 'center', color: '#999' }}>
              No modules found
            </Box>
          )}
        </FeatureList>

        <ActionButtons>
          <CancelButton onClick={() => navigate('/super-admin/subscriptions')}>
            Cancel
          </CancelButton>
          <CreateButton 
            variant="contained" 
            disableElevation
            onClick={handleSubmit}
            disabled={!formData.plan_name || enabledCount === 0}
          >
            Create Plan
          </CreateButton>
        </ActionButtons>
      </FormContainer>
    </PageWrapper>
  );
};

export default CreateSubscriptionPage;
